import React from 'react';
import type { BureauCredential } from '../../../types';
import {
  BUREAUS,
  BUREAU_LABELS,
  formatBureauStageDisplay,
  isLockedOrBlockedBureau,
  isUnblockedBureau,
} from '../creditBlockClientConstants';
import { CreditBlockFlowModalLayout, ToolbarFlowSubmitButton } from './CreditBlockFlowModalLayout';

export const CaseOwnerPickerTable: React.FC<{
  title: string;
  ownerOptions: Array<{ value: string; label: string }>;
  selectedOwner: string;
  onSelectOwner: (owner: string) => void;
}> = ({ title, ownerOptions, selectedOwner, onSelectOwner }) => {
  const rows = ownerOptions.map((option) => {
    const fullName = String(option.label ?? '').replace(/\s*\(you\)\s*$/i, '').trim();
    const parts = fullName.trim().split(/\s+/);
    const firstName = parts[0] || fullName;
    const lastName = parts.slice(1).join(' ') || '—';
    return { id: option.value, displayName: fullName, firstName, lastName };
  });

  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded-sm">
      <div className="px-3 py-2 border-b border-gray-300 dark:border-gray-600 flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            1 of {rows.length} item{rows.length === 1 ? '' : 's'} selected
          </p>
        </div>
        <input
          type="text"
          placeholder="Search this list..."
          className="w-52 px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          disabled
        />
      </div>
      <table className="w-full text-xs">
        <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-300 dark:border-gray-600">
          <tr>
            <th className="w-9 px-2 py-1.5" />
            <th className="px-2 py-1.5 text-left font-medium text-gray-600 dark:text-gray-300">First Name</th>
            <th className="px-2 py-1.5 text-left font-medium text-gray-600 dark:text-gray-300">Last Name</th>
            <th className="px-2 py-1.5 text-left font-medium text-gray-600 dark:text-gray-300">Department</th>
            <th className="px-2 py-1.5 text-left font-medium text-gray-600 dark:text-gray-300">Title</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b last:border-b-0 border-gray-200 dark:border-gray-700">
              <td className="px-2 py-1.5">
                <button
                  type="button"
                  onClick={() => onSelectOwner(row.id)}
                  className={`w-4 h-4 rounded-sm border ${
                    selectedOwner === row.id
                      ? 'border-blue-600 bg-blue-600'
                      : 'border-gray-400 bg-white dark:bg-gray-900'
                  }`}
                  aria-label={`Select ${row.displayName}`}
                >
                  {selectedOwner === row.id ? <span className="block w-full h-full text-[9px] leading-4 text-white">✓</span> : null}
                </button>
              </td>
              <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100">{row.firstName}</td>
              <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100">{row.lastName}</td>
              <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">Credit block</td>
              <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">—</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const FirstTimeBlockOwnerModal: React.FC<{
  open: boolean;
  ownerOptions: Array<{ value: string; label: string }>;
  selectedOwner: string;
  onSelectOwner: (owner: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  submitting?: boolean;
}> = ({ open, ownerOptions, selectedOwner, onSelectOwner, onClose, onSubmit, submitting = false }) => {
  if (!open) return null;
  return (
    <CreditBlockFlowModalLayout
      title="First Time Block"
      submitting={submitting}
      onClose={onClose}
      panelClassName="max-w-3xl"
      footer={
        <ToolbarFlowSubmitButton
          label="Assign Case Owner"
          submittingLabel="Creating case..."
          submitting={submitting}
          onClick={onSubmit}
        />
      }
    >
      <CaseOwnerPickerTable
        title="Select First Time Block Case Owner"
        ownerOptions={ownerOptions}
        selectedOwner={selectedOwner}
        onSelectOwner={onSelectOwner}
      />
    </CreditBlockFlowModalLayout>
  );
};

export const PermanentUnblockOwnerModal: React.FC<{
  open: boolean;
  ownerOptions: Array<{ value: string; label: string }>;
  selectedOwner: string;
  onSelectOwner: (owner: string) => void;
  requestSource: string;
  onRequestSourceChange: (value: string) => void;
  selectedBureaus: Record<string, boolean>;
  onToggleBureau: (bureau: 'equifax' | 'experian' | 'transunion' | 'chexsystems') => void;
  onClose: () => void;
  onSubmit: () => void;
  submitting?: boolean;
}> = ({
  open,
  ownerOptions,
  selectedOwner,
  onSelectOwner,
  requestSource,
  onRequestSourceChange,
  selectedBureaus,
  onToggleBureau,
  onClose,
  onSubmit,
  submitting = false,
}) => {
  if (!open) return null;
  const selectedCount = BUREAUS.filter((b) => selectedBureaus[b]).length;
  return (
    <CreditBlockFlowModalLayout
      title="Permanent Unblock"
      submitting={submitting}
      onClose={onClose}
      panelClassName="max-w-3xl"
      footer={
        <ToolbarFlowSubmitButton
          label="Create Perm Unblock Cases"
          submittingLabel="Creating cases..."
          submitting={submitting}
          onClick={onSubmit}
        />
      }
    >
      <div className="space-y-4">
        <CaseOwnerPickerTable
          title="Select Permanent Unblock Case Owner"
          ownerOptions={ownerOptions}
          selectedOwner={selectedOwner}
          onSelectOwner={onSelectOwner}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            <span className="text-red-500">*</span> Request Source
          </label>
          <select
            value={requestSource}
            onChange={(e) => onRequestSourceChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          >
            <option value="">--None--</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
          </select>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            <span className="text-red-500">*</span> Select Bureaus to Permanently Unblock
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({selectedCount} of {BUREAUS.length} selected)</span>
          </p>
          <div className="space-y-1">
            {BUREAUS.map((bureau) => (
              <label key={bureau} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={!!selectedBureaus[bureau]}
                  onChange={() => onToggleBureau(bureau)}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                {BUREAU_LABELS[bureau]}
              </label>
            ))}
          </div>
        </div>
      </div>
    </CreditBlockFlowModalLayout>
  );
};

export const UnblockBureausModal: React.FC<{
  open: boolean;
  requestSource: string;
  onRequestSourceChange: (value: string) => void;
  startDate: string;
  onStartDateChange: (value: string) => void;
  endDate: string;
  onEndDateChange: (value: string) => void;
  endDateLength: string;
  onEndDateLengthChange: (value: string) => void;
  selectedBureaus: Record<string, boolean>;
  bureauCredentials: Record<string, BureauCredential>;
  onToggleBureau: (bureau: 'equifax' | 'experian' | 'transunion' | 'chexsystems') => void;
  onClose: () => void;
  onSubmit: () => void;
  submitting?: boolean;
}> = ({
  open,
  requestSource,
  onRequestSourceChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  endDateLength,
  onEndDateLengthChange,
  selectedBureaus,
  bureauCredentials,
  onToggleBureau,
  onClose,
  onSubmit,
  submitting = false,
}) => {
  if (!open) return null;
  const eligibleBureaus = BUREAUS.filter((bureau) => !isUnblockedBureau(bureauCredentials[bureau]));
  const selectedCount = eligibleBureaus.filter((b) => selectedBureaus[b]).length;

  return (
    <CreditBlockFlowModalLayout
      title="Unblock Bureaus"
      submitting={submitting}
      onClose={onClose}
      footer={
        <ToolbarFlowSubmitButton
          label="Unblock Process"
          submittingLabel="Unblock in progress..."
          submitting={submitting}
          onClick={onSubmit}
        />
      }
    >
      <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800">
        <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Request & bureau selection</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Fill in the request below, then choose bureaus. Submit uses the button at the bottom of this dialog.
          </p>
        </div>
        <div className="p-4 space-y-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <span className="text-red-500">*</span> Request Source
              </label>
              <select
                value={requestSource}
                onChange={(e) => onRequestSourceChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              >
                <option value="">--None--</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <span className="text-red-500">*</span> Unblock Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => onStartDateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <span className="text-red-500">*</span> Unblock End Date Length
              </label>
              <select
                value={endDateLength}
                onChange={(e) => onEndDateLengthChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              >
                <option value="">--None--</option>
                <option value="24_hours">24 Hours</option>
                <option value="72_hours">72 Hours</option>
                <option value="7_days">7 Days</option>
                <option value="30_days">30 Days</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            {endDateLength === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <span className="text-red-500">*</span> Custom End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => onEndDateChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
              Bureaus to unblock
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                ({selectedCount} of {eligibleBureaus.length} eligible selected)
              </span>
            </p>
            {eligibleBureaus.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No locked bureaus available to unblock.</p>
            ) : (
              <div className="rounded-md border border-gray-200 dark:border-gray-600 divide-y divide-gray-200 dark:divide-gray-600">
                <div className="grid grid-cols-[2rem_1fr_5.5rem_5.5rem] gap-2 px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/40">
                  <span />
                  <span>Bureau</span>
                  <span>Authority</span>
                  <span>Stage</span>
                </div>
                {eligibleBureaus.map((bureau) => {
                  const cred = bureauCredentials[bureau];
                  const authority = cred?.authority ? (cred.authority === 'primary' ? 'Primary' : 'Secondary') : 'Primary';
                  const stage = formatBureauStageDisplay(cred) || 'Locked';
                  return (
                    <label
                      key={bureau}
                      className="grid grid-cols-[2rem_1fr_5.5rem_5.5rem] gap-2 px-3 py-2.5 items-center text-sm text-gray-900 dark:text-gray-100 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30"
                    >
                      <input
                        type="checkbox"
                        checked={!!selectedBureaus[bureau]}
                        onChange={() => onToggleBureau(bureau)}
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                      <span>{BUREAU_LABELS[bureau]}</span>
                      <span className="text-gray-600 dark:text-gray-300">{authority}</span>
                      <span className="text-gray-600 dark:text-gray-300">{stage}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </CreditBlockFlowModalLayout>
  );
};

export const ExtensionBureausModal: React.FC<{
  open: boolean;
  requestSource: string;
  onRequestSourceChange: (value: string) => void;
  startDate: string;
  onStartDateChange: (value: string) => void;
  endDate: string;
  onEndDateChange: (value: string) => void;
  endDateLength: string;
  onEndDateLengthChange: (value: string) => void;
  selectedBureaus: Record<string, boolean>;
  bureauCredentials: Record<string, BureauCredential>;
  onToggleBureau: (bureau: 'equifax' | 'experian' | 'transunion' | 'chexsystems') => void;
  onClose: () => void;
  onSubmit: () => void;
  submitting?: boolean;
}> = ({
  open,
  requestSource,
  onRequestSourceChange,
  startDate: _startDate,
  onStartDateChange: _onStartDateChange,
  endDate,
  onEndDateChange,
  endDateLength,
  onEndDateLengthChange,
  selectedBureaus,
  bureauCredentials,
  onToggleBureau,
  onClose,
  onSubmit,
  submitting = false,
}) => {
  if (!open) return null;
  const eligibleBureaus = BUREAUS.filter((bureau) => isUnblockedBureau(bureauCredentials[bureau]));
  const selectedCount = eligibleBureaus.filter((b) => selectedBureaus[b]).length;
  const fmtLocalDate = (v: string | undefined) => {
    if (!v) return '—';
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString();
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
  };

  return (
    <CreditBlockFlowModalLayout
      title="Unblock Extension"
      submitting={submitting}
      onClose={onClose}
      footer={
        <ToolbarFlowSubmitButton
          label="Extension Process"
          submittingLabel="Extension in progress..."
          submitting={submitting}
          onClick={onSubmit}
        />
      }
    >
      <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800">
        <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Extension & bureau selection</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Set the new end date (or length), then pick bureaus. Submit stays at the bottom of this dialog.
          </p>
        </div>
        <div className="p-4 space-y-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <span className="text-red-500">*</span> Request Source
              </label>
              <select
                value={requestSource}
                onChange={(e) => onRequestSourceChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              >
                <option value="">--None--</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <span className="text-red-500">*</span> New End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => onEndDateChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Or Extend By</label>
                <select
                  value={endDateLength}
                  onChange={(e) => onEndDateLengthChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                >
                  <option value="">--None--</option>
                  <option value="24_hours">24 Hours</option>
                  <option value="72_hours">72 Hours</option>
                  <option value="7_days">7 Days</option>
                  <option value="30_days">30 Days</option>
                </select>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
              Bureaus to extend
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                ({selectedCount} of {eligibleBureaus.length} eligible selected)
              </span>
            </p>
            {eligibleBureaus.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No unblocked bureaus available for extension.</p>
            ) : (
              <div className="rounded-md border border-gray-200 dark:border-gray-600 divide-y divide-gray-200 dark:divide-gray-600">
                <div className="grid grid-cols-[2rem_1fr_5rem_5.5rem_5.5rem] gap-2 px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/40">
                  <span />
                  <span>Bureau</span>
                  <span>Authority</span>
                  <span>Start</span>
                  <span>End</span>
                </div>
                {eligibleBureaus.map((bureau) => {
                  const cred = bureauCredentials[bureau];
                  const authority = cred?.authority ? (cred.authority === 'primary' ? 'Primary' : 'Secondary') : 'Primary';
                  const currentStart = cred?.startDate || cred?.enrollmentDate || '';
                  const currentEnd = cred?.endDate || cred?.unblockDate || '';
                  return (
                    <label
                      key={bureau}
                      className="grid grid-cols-[2rem_1fr_5rem_5.5rem_5.5rem] gap-2 px-3 py-2.5 items-center text-sm text-gray-900 dark:text-gray-100 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30"
                    >
                      <input
                        type="checkbox"
                        checked={!!selectedBureaus[bureau]}
                        onChange={() => onToggleBureau(bureau)}
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                      <span>{BUREAU_LABELS[bureau]}</span>
                      <span className="text-gray-600 dark:text-gray-300">{authority}</span>
                      <span className="text-gray-600 dark:text-gray-300">{fmtLocalDate(currentStart)}</span>
                      <span className="text-gray-600 dark:text-gray-300">{fmtLocalDate(currentEnd)}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </CreditBlockFlowModalLayout>
  );
};

export const BlockBureausModal: React.FC<{
  open: boolean;
  requestSource: string;
  onRequestSourceChange: (value: string) => void;
  selectedBureaus: Record<string, boolean>;
  bureauCredentials: Record<string, BureauCredential>;
  onToggleBureau: (bureau: 'equifax' | 'experian' | 'transunion' | 'chexsystems') => void;
  onClose: () => void;
  onSubmit: () => void;
  submitting?: boolean;
}> = ({
  open,
  requestSource,
  onRequestSourceChange,
  selectedBureaus,
  bureauCredentials,
  onToggleBureau,
  onClose,
  onSubmit,
  submitting = false,
}) => {
  if (!open) return null;
  const eligibleBureaus = BUREAUS.filter((bureau) => !isLockedOrBlockedBureau(bureauCredentials[bureau]));
  const selectedCount = eligibleBureaus.filter((b) => selectedBureaus[b]).length;

  return (
    <CreditBlockFlowModalLayout
      title="Block Bureaus"
      submitting={submitting}
      onClose={onClose}
      footer={
        <ToolbarFlowSubmitButton
          label="Block Process"
          submittingLabel="Block in progress..."
          submitting={submitting}
          onClick={onSubmit}
        />
      }
    >
      <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800">
        <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Request & bureau selection</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Choose a request source, select bureaus, then submit with the button at the bottom.
          </p>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <span className="text-red-500">*</span> Request Source
            </label>
            <select
              value={requestSource}
              onChange={(e) => onRequestSourceChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            >
              <option value="">--None--</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
            </select>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
              Bureaus to block
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                ({selectedCount} of {eligibleBureaus.length} eligible selected)
              </span>
            </p>
            {eligibleBureaus.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No unlocked bureaus available to block.</p>
            ) : (
              <div className="rounded-md border border-gray-200 dark:border-gray-600 divide-y divide-gray-200 dark:divide-gray-600">
                <div className="grid grid-cols-[2rem_1fr_5.5rem_5.5rem] gap-2 px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/40">
                  <span />
                  <span>Bureau</span>
                  <span>Authority</span>
                  <span>Stage</span>
                </div>
                {eligibleBureaus.map((bureau) => {
                  const cred = bureauCredentials[bureau];
                  const authority = cred?.authority ? (cred.authority === 'primary' ? 'Primary' : 'Secondary') : 'Primary';
                  const stage = formatBureauStageDisplay(cred) || 'Unlocked';
                  return (
                    <label
                      key={bureau}
                      className="grid grid-cols-[2rem_1fr_5.5rem_5.5rem] gap-2 px-3 py-2.5 items-center text-sm text-gray-900 dark:text-gray-100 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30"
                    >
                      <input
                        type="checkbox"
                        checked={!!selectedBureaus[bureau]}
                        onChange={() => onToggleBureau(bureau)}
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                      <span>{BUREAU_LABELS[bureau]}</span>
                      <span className="text-gray-600 dark:text-gray-300">{authority}</span>
                      <span className="text-gray-600 dark:text-gray-300">{stage}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </CreditBlockFlowModalLayout>
  );
};
